import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ColumnDef } from "@tanstack/react-table";
import { apiFetch } from "@/api/client";
import {
  listClientsFull,
  listEmployeesFull,
  listListAnalyses,
  listMeetingTypesFull,
  listMeetingsQS,
  type MeetingsListQuery,
} from "@/api/lookups";
import type { MeetingSummary, UUID } from "@/api/types";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/table/DataTable";
import {
  makeMeetingSelectColumn,
  useMeetingSelection,
} from "@/components/table/meetingSelection";
import { UploadDialog } from "@/components/upload/UploadDialog";
import { ListAnalysisDialog } from "@/components/analysis/ListAnalysisDialog";
import { useCan } from "@/hooks/useCan";
import { useFolders } from "@/hooks/useFolders";
import {
  MeetingsFilters,
  type MeetingsFilterValues,
} from "@/pages/registries/MeetingsFilters";
import { useMeetingColumnsFrom } from "@/pages/registries/useMeetingColumns";

const EMPTY: MeetingsFilterValues = {
  since: "",
  until: "",
  client_id: "",
  meeting_type_id: "",
  participant_id: "",
  status: "",
};

/**
 * Expand a ``YYYY-MM-DD`` date input value to an end-of-day ISO timestamp.
 *
 * MUST-FIX (review): the filter form uses ``<input type=date>`` which yields a
 * bare ``YYYY-MM-DD`` string. Sending that to the API was parsed as midnight
 * UTC, which made ``created_at <= until`` exclude the entire selected day.
 * We send ``T23:59:59.999Z`` to keep the contract "inclusive end of day".
 *
 * If the user types a full ISO timestamp (e.g. from URL state), pass it
 * through unchanged.
 */
function expandUntilToEndOfDay(raw: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return `${raw}T23:59:59.999Z`;
  return raw;
}

function toQuery(f: MeetingsFilterValues): MeetingsListQuery {
  const q: MeetingsListQuery = {};
  if (f.since) q.since = f.since;
  if (f.until) q.until = expandUntilToEndOfDay(f.until);
  if (f.client_id) q.client_id = f.client_id;
  if (f.meeting_type_id) q.meeting_type_id = f.meeting_type_id;
  if (f.participant_id) q.participant_id = f.participant_id;
  if (f.status) q.status = f.status;
  return q;
}

function fetchMeetings(
  q: MeetingsListQuery,
  activeFolderId: UUID | null,
  sort: string,
  order: "asc" | "desc",
): Promise<MeetingSummary[]> {
  // Papki-tenants v1: scope the registry to the active folder via BOTH
  // the `X-Active-Folder` request header (the canonical backend
  // transport, picked up by the folder-scoped middleware) AND the
  // `folder_id` query param. Belt-and-braces makes the contract robust
  // to a header-stripping reverse proxy (we've been bitten by that with
  // nginx in front of FastAPI before) and matches the test fixture's
  // permissive "either transport is OK" parser.
  const withFolder: MeetingsListQuery =
    activeFolderId !== null ? { ...q, folder_id: activeFolderId } : q;
  // TM-035ae774: always send sort/order so the BE applies the same
  // whitelist-validated order_by (and the stable meeting_no tie-breaker)
  // the registry expects. The default state (created_at/desc) is identical
  // to the BE default, so this stays back-compatible.
  const withSort: MeetingsListQuery = { ...withFolder, sort, order };
  const headers: Record<string, string> =
    activeFolderId !== null ? { "X-Active-Folder": activeFolderId } : {};
  return apiFetch<MeetingSummary[]>(
    `/api/meetings${listMeetingsQS(withSort)}`,
    {
      method: "GET",
      headers,
    },
  );
}

/**
 * Trigger a CSV download of the current registry view (T2 / TaskMe 5313eae7).
 *
 * We use ``window.location.assign`` instead of an ``apiFetch`` -> ``Blob``
 * round-trip because the export can be large and we want the browser's native
 * download UX (progress bar, partial-content resume) rather than buffering
 * the whole CSV in JS. The endpoint sets ``Content-Disposition: attachment``
 * so the navigation does not unmount the SPA.
 *
 * The filter set passed here is the *applied* filter state (the one already
 * sent to ``GET /api/meetings``), not the draft, so the operator gets exactly
 * the rows they currently see on screen.
 */
function downloadMeetingsCsv(q: MeetingsListQuery): void {
  // ``listMeetingsQS`` returns ``""`` or ``"?...".`` Either way concatenation
  // produces a valid URL — ``/api/meetings/export`` with or without a query.
  window.location.assign(`/api/meetings/export${listMeetingsQS(q)}`);
}

export function MeetingsRegistry() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canUpload = useCan("meeting_upload");
  const canListAnalyze = useCan("list_analysis_create");
  const [filters, setFilters] = useState<MeetingsFilterValues>(EMPTY);
  const [draft, setDraft] = useState<MeetingsFilterValues>(EMPTY);
  const [uploadOpen, setUploadOpen] = useState(false);
  const selection = useMeetingSelection();
  const [listDialogOpen, setListDialogOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // TM-035ae774: server-side sort state. Default mirrors the backend's
  // `created_at desc`; both are threaded into the query key so a header
  // click triggers a refetch with the new `&sort=&order=`.
  const [sort, setSort] = useState("created_at");
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  // Papki-tenants v1: folder scope drives the meetings query key + header.
  const { folders, activeFolderId } = useFolders();

  // TM-035ae774: clicking a sortable header toggles direction when it's the
  // active column, otherwise switches column and starts at `desc` (the
  // single default for every sortable column, matching the backend).
  const onSortChange = useCallback(
    (key: string) => {
      if (key === sort) {
        setOrder((o) => (o === "asc" ? "desc" : "asc"));
      } else {
        setSort(key);
        setOrder("desc");
      }
    },
    [sort],
  );

  // TM-35822a0e: whole-row navigation to the meeting card, mirroring the
  // Employees/Clients registries' `onRowClick` pattern. The existing
  // `<Link>` in the «Файл» column already navigates there — this just
  // extends the affordance to every other cell so operators don't have to
  // aim for the filename text. The router param name (`meetingId`) matches
  // the route declaration in `src/routes/meetings.$meetingId.tsx`.
  const goToMeeting = useCallback(
    (row: MeetingSummary) => {
      void navigate({
        to: "/meetings/$meetingId",
        params: { meetingId: row.id },
      });
    },
    [navigate],
  );

  const meetings = useQuery({
    // Include `activeFolderId` in the key so a folder switch produces a
    // fresh cache entry and unrelated tabs don't flash stale data while
    // the new fetch is in flight.
    queryKey: ["meetings", "list", filters, activeFolderId, sort, order],
    queryFn: () => fetchMeetings(toQuery(filters), activeFolderId, sort, order),
    // UI_PLAN §6.4: until backend exposes a registry-channel, poll-on-interval
    // is the simple v1 strategy.
    refetchInterval: 30_000,
  });

  const clients = useQuery({
    queryKey: ["clients-lookup"],
    queryFn: () => listClientsFull({ limit: 200 }),
  });

  const types = useQuery({
    queryKey: ["meeting-types-lookup"],
    queryFn: () => listMeetingTypesFull(),
  });

  // T1 / FR-26: employees power both the «Участники» filter and the
  // fallback rendering when ``participants_display`` is empty for some
  // legacy reason.
  const employees = useQuery({
    queryKey: ["employees-lookup", "registry-meetings"],
    queryFn: () => listEmployeesFull({ limit: 500 }),
  });

  // Feature-probe for list-analysis (TM-4c1fb82b): 404 → hide the checkbox
  // column and the «Анализ выбранных» button.
  const listProbeQ = useQuery({
    queryKey: ["list-analyses", "probe", "registry"],
    queryFn: () => listListAnalyses({ limit: 1 }),
    enabled: canListAnalyze,
    retry: false,
    staleTime: 60_000,
  });
  const listFeatureEnabled =
    canListAnalyze && listProbeQ.isSuccess && Array.isArray(listProbeQ.data);

  const baseColumns = useMeetingColumnsFrom(
    clients.data ?? [],
    types.data ?? [],
    folders,
  );
  const columns = useMemo<ColumnDef<MeetingSummary, unknown>[]>(() => {
    if (!listFeatureEnabled) return baseColumns;
    return [makeMeetingSelectColumn(selection), ...baseColumns];
  }, [baseColumns, listFeatureEnabled, selection]);

  return (
    // TM-0ed9840f — the meetings registry holds the widest tables in the app
    // (client, type, participants, status, dates…). The shell caps every page
    // at `max-w-screen-2xl`, which on wide monitors leaves dead space on the
    // right and squeezes these columns. We let *only this page* stretch to the
    // right viewport edge by pinning its width to the viewport minus the
    // shell's fixed left offset (px-4 always; +sidebar w-56 +gap-6 from md up
    // where the sidebar is visible) and a 1rem right breather. This is
    // adaptive to window width and page-scoped — cards/admin/forms keep their
    // readable capped column. The table's own `overflow-x-auto` still guards
    // narrow viewports.
    <div className="w-[calc(100vw-2rem)] space-y-4 md:w-[calc(100vw-18.5rem)]">
      <header className="flex items-end justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold">
            {t("meetings.registryTitle")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t("meetings.registrySubtitle")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/*
            T2: «Экспорт CSV» mirrors the registry's applied filters. We tie
            it to ``meeting_list`` semantics by gating on the same flag — if
            the user can see the list they can export it. The backend re-checks
            with ``require_role(Action.meeting_list)`` so this is purely UI
            ergonomics, not a security boundary.
          */}
          <Button
            type="button"
            variant="outline"
            onClick={() => downloadMeetingsCsv(toQuery(filters))}
            data-testid="export-meetings-csv"
          >
            {t("meetings.actions.exportCsv")}
          </Button>
          {listFeatureEnabled ? (
            <Button
              type="button"
              variant="outline"
              disabled={selection.count === 0}
              onClick={() => setListDialogOpen(true)}
              data-testid="meetings-list-analyze-button"
            >
              {selection.count > 0
                ? t("listAnalysis.section.analyzeSelected", {
                    count: selection.count,
                  })
                : t("listAnalysis.section.analyzeZero")}
            </Button>
          ) : null}
          {canUpload ? (
            <Button
              type="button"
              onClick={() => setUploadOpen(true)}
              data-testid="open-upload"
            >
              {t("meetings.uploadButton")}
            </Button>
          ) : null}
        </div>
      </header>

      {submitted ? (
        <div
          role="status"
          className="flex items-center justify-between gap-2 rounded-md border border-green-500/30 bg-green-500/10 px-3 py-2 text-sm text-green-800 dark:text-green-300"
          data-testid="meetings-list-analyze-submitted"
        >
          <span>
            {t("listAnalysis.toast.submitted")}{" "}
            <Link
              to="/analyses"
              className="font-medium underline underline-offset-4"
            >
              {t("listAnalysis.toast.link")}
            </Link>
            .
          </span>
          <button
            type="button"
            className="text-xs text-muted-foreground hover:text-foreground"
            onClick={() => setSubmitted(false)}
            aria-label={t("common.close")}
          >
            ×
          </button>
        </div>
      ) : null}

      <MeetingsFilters
        draft={draft}
        onChange={setDraft}
        onApply={() => setFilters(draft)}
        clients={clients.data ?? []}
        types={types.data ?? []}
        employees={employees.data ?? []}
      />

      <DataTable
        data={meetings.data ?? []}
        columns={columns}
        testId="meetings-table"
        isLoading={meetings.isLoading}
        onRowClick={goToMeeting}
        sort={sort}
        order={order}
        onSortChange={onSortChange}
      />

      <UploadDialog open={uploadOpen} onOpenChange={setUploadOpen} />

      {listFeatureEnabled ? (
        <ListAnalysisDialog
          open={listDialogOpen}
          mode="free"
          selectedMeetingIds={Array.from(selection.selectedIds)}
          onClose={() => setListDialogOpen(false)}
          onSubmitted={() => {
            selection.clear();
            setSubmitted(true);
          }}
        />
      ) : null}
    </div>
  );
}
